import { CallbackAsync, CspError, Maybe, StateAssert, User } from '@csp/csp-common-model';
import {
  CaptureMessage,
  ExceptionTracker,
  TelemetryContext,
  TelemetryException,
  TelemetryServiceOptions,
  TelemetryServicePlugin,
  TelemetryTags,
  emptyExceptionTracker,
} from '@csp/csp-common-telemetry';
import * as Sentry from '@sentry/react';
import { TransactionContext } from '@sentry/types';
import * as React from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

interface WebTelemetryServicePlugin extends TelemetryServicePlugin {
  type: 'WebTelemetryServicePlugin';
}

const assertHasText = (dsn: string): string => {
  StateAssert.hasText(dsn, 'No Data Source Name (DSN) configured.');
  return dsn;
};

const toRoutePath = (path: string): { path: string } => ({ path });

const createTelemetryServicePlugin = (): WebTelemetryServicePlugin => {
  const isSentryEnabled = (): boolean => !!Sentry.getCurrentHub().getClient();

  const init = ({ dsn = '', routePaths, ...otherOptions }: TelemetryServiceOptions): void => {
    try {
      /**
       * Maps a location.pathname like /patient/6fb2976ae05dce5acf9fdff1/overview
       * to its react-router-dom route counterpart /patient/:patientId/overview
       */
      const matchLocationPathNameToRoutePath = (): string => {
        const reactRouterRoutePaths = routePaths && Object.values(routePaths)?.map(toRoutePath);
        if (reactRouterRoutePaths) {
          const routeMatch = matchRoutes(reactRouterRoutePaths, location.pathname);
          return routeMatch?.[0]?.route.path ?? location.pathname;
        } else {
          return location.pathname;
        }
      };
      Sentry.init({
        dsn: assertHasText(dsn),
        integrations: [
          new Sentry.BrowserTracing({
            routingInstrumentation: Sentry.reactRouterV6Instrumentation(
              React.useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
            ),
            beforeNavigate: (context: TransactionContext): TransactionContext | undefined => ({
              ...context,
              name: matchLocationPathNameToRoutePath(),
            }),
          }),
          new Sentry.Integrations.Breadcrumbs({
            console: false,
          }),
        ],
        ignoreErrors: ['Non-Error promise rejection captured with value:'],
        ...otherOptions,
      });
      console.debug('[Sentry.io] is enabled.');
    } catch (e) {
      console.debug('[Sentry.io] could not be loaded.', e);
    }
  };

  const configureScope = (user: Maybe<User>, telemetryTags: Maybe<TelemetryTags>): void => {
    Sentry.configureScope(scope => {
      scope.setUser({ id: user?.userId });
      scope.setTags(telemetryTags ?? {});
    });
  };

  const getExceptionTracker = (): ExceptionTracker & CaptureMessage => {
    if (isSentryEnabled()) {
      return {
        trackException: ({ error, context }: TelemetryException): string | undefined => {
          let sentryEventId: string | undefined = undefined;
          Sentry.withScope(scope => {
            if (context !== undefined) {
              const { extra, tags } = context as TelemetryContext;
              if (extra) {
                scope.setExtras(extra);
              }
              if (tags) {
                scope.setTags(tags);
              }
            }
            sentryEventId = Sentry.captureException(error);
          });
          return sentryEventId;
        },

        captureMessage: (message: string): void => {
          Sentry.captureMessage(message);
        },
      };
    } else {
      console.debug('Please call init(). [Sentry.io] has not been loaded.');
      return emptyExceptionTracker;
    }
  };

  const flush = async (timeout?: number): Promise<boolean> => await Sentry.flush(timeout);

  const reset: CallbackAsync = async () => {
    if (isSentryEnabled()) {
      Sentry.configureScope(scope => scope.clear());
      await Sentry.flush();
    }
  };

  const trackException = (exception: TelemetryException): string | undefined =>
    getExceptionTracker().trackException({
      ...exception,
    });

  const captureMessage = (message: string): void => {
    const exceptionTracker = getExceptionTracker();
    if (exceptionTracker.captureMessage) {
      exceptionTracker.captureMessage(message);
    } else {
      throw CspError.badState('Capture message undefined');
    }
  };

  return {
    type: 'WebTelemetryServicePlugin',
    init,
    configureScope,
    flush,
    reset,
    trackException,
    captureMessage,
  };
};

export const WebTelemetryServicePlugin = createTelemetryServicePlugin();
